import { updatePlayerSpeed } from "@app/store/playerStore.svelte.js";

const FF_RATE = 8;
const REWIND_STEP_SEC = 0.25;
const REWIND_INTERVAL_MS = 50;

/** @type {ReturnType<typeof setInterval> | undefined} */
let rewindInterval;
/** @type {'idle' | 'playing' | 'fastForward' | 'rewind'} */
let transportMode = "idle";

function getPlayer() {
    return typeof window !== "undefined" ? window.player : null;
}

export function getTransportMode() {
    return transportMode;
}

export function isRewinding() {
    return transportMode === "rewind";
}

export function isFastForwarding() {
    return transportMode === "fastForward";
}

async function safePlay(player) {
    try {
        await player.play();
    } catch (err) {
        console.warn("[pedal transport] play failed", err);
    }
}

async function safePause(player) {
    try {
        await player.pause();
    } catch (err) {
        console.warn("[pedal transport] pause failed", err);
    }
}

function clearRewindInterval() {
    if (rewindInterval) {
        clearInterval(rewindInterval);
        rewindInterval = undefined;
    }
}

function resetPlaybackRate(player) {
    player.playbackRate = 1;
    updatePlayerSpeed(1);
}

/**
 * Stop FF/rewind intervals and restore normal rate without necessarily pausing.
 */
export function releaseScrubModes() {
    const player = getPlayer();
    clearRewindInterval();
    if (player && (transportMode === "fastForward" || transportMode === "rewind")) {
        resetPlaybackRate(player);
    }
    if (transportMode === "fastForward" || transportMode === "rewind") {
        transportMode = "idle";
    }
}

export async function play() {
    const player = getPlayer();
    if (!player) return;

    clearRewindInterval();
    resetPlaybackRate(player);
    transportMode = "playing";
    await safePlay(player);
}

export async function pause() {
    const player = getPlayer();
    if (!player) return;

    clearRewindInterval();
    resetPlaybackRate(player);
    transportMode = "idle";
    await safePause(player);
}

export async function togglePlay() {
    const player = getPlayer();
    if (!player) return;

    const playing =
        player.state?.playing ??
        (typeof player.paused === "boolean" ? !player.paused : false);

    if (playing || transportMode === "playing" || transportMode === "fastForward" || transportMode === "rewind") {
        await pause();
    } else {
        await play();
    }
}

export async function startFastForward() {
    const player = getPlayer();
    if (!player) return;

    clearRewindInterval();
    player.playbackRate = FF_RATE;
    updatePlayerSpeed(FF_RATE);
    transportMode = "fastForward";
    await safePlay(player);
}

export async function stopFastForward() {
    const player = getPlayer();
    if (!player) return;

    if (transportMode !== "fastForward" && player.playbackRate === 1) {
        return;
    }

    resetPlaybackRate(player);
    transportMode = "idle";
    await safePause(player);
}

export async function toggleFastForward() {
    if (transportMode === "fastForward" || (getPlayer()?.playbackRate ?? 1) !== 1) {
        await stopFastForward();
    } else {
        await startFastForward();
    }
}

export async function startRewind() {
    const player = getPlayer();
    if (!player) return;

    clearRewindInterval();
    resetPlaybackRate(player);
    transportMode = "rewind";

    rewindInterval = setInterval(() => {
        const p = getPlayer();
        if (!p) {
            clearRewindInterval();
            return;
        }
        const next = Math.max(0, (p.currentTime || 0) - REWIND_STEP_SEC);
        p.currentTime = next;
        safePlay(p);
    }, REWIND_INTERVAL_MS);
}

export async function stopRewind() {
    const player = getPlayer();
    clearRewindInterval();

    if (!player) {
        transportMode = "idle";
        return;
    }

    if (transportMode === "rewind") {
        resetPlaybackRate(player);
        transportMode = "idle";
        await safePause(player);
    }
}

export async function toggleRewind() {
    if (transportMode === "rewind") {
        await stopRewind();
    } else {
        await startRewind();
    }
}

/**
 * Release all hold actions (pause + clear scrub modes).
 */
export async function releaseAll() {
    await pause();
}

/**
 * Apply a pedal event with hold semantics.
 * @param {{ action: string, phase: 'down' | 'up' | 'change' }} event
 */
export async function handlePedalEvent(event) {
    const { action, phase } = event;

    if (action === "none" || !action) {
        return;
    }

    if (phase === "up") {
        switch (action) {
            case "play":
                await pause();
                break;
            case "fastForward":
                await stopFastForward();
                break;
            case "rewind":
                await stopRewind();
                break;
            case "pause":
                await pause();
                break;
            default:
                break;
        }
        return;
    }

    // down or change
    switch (action) {
        case "play":
            await play();
            break;
        case "pause":
            await pause();
            break;
        case "fastForward":
            await startFastForward();
            break;
        case "rewind":
            await startRewind();
            break;
        default:
            break;
    }
}
